export interface ProductCatalogEntry {
  /** Product key. Uppercased it becomes the credit type; lowercased it forms the history field. */
  key: string;
  /** Translation key for the display name, in the Common module. */
  name: string;
  image: string;
  /** Field on TenantBalances holding the tenant-wide remaining units. */
  tenantBalance: string;
  /** Field on BalanceDistribution holding the company's allocated units. */
  currentBalance: string;
  /** Field on BalanceDistribution holding the company's consumed units. */
  usedBalance: string;
  /**
   * Survey name written onto a unit request, which is what the admin approving it reads.
   * Not translated: it is stored on the request record, not rendered from a key.
   */
  requestName: string;
}

/**
 * The single source of truth for the products that carry a unit balance, in display order.
 *
 * Feeds the account overview (tenant sidebar, per-company allocation table, purchase
 * history) and, from outside this feature, the shared request-units dialog. Adding a
 * product here is the whole change — before this was shared, the dialog carried its own
 * parallel list, its own map of amount-input keys and its own map of request names, and
 * Culture was missing from all three while the account overview had it.
 *
 * Not the same list as the header's nav routes: those are keyed for routing and carry
 * products with no balance. Deliberately kept apart.
 *
 * Assessment covers both 360 and 180 feedback; they were split until the two
 * balances were merged behind `assessmentBalance`.
 */
export const PRODUCT_CATALOG: ProductCatalogEntry[] = [
  { key: 'Onboarding', name: 'txtOnBoarding', image: '/assets/img/sub/onboarding.svg', tenantBalance: 'onboardingBalance', currentBalance: 'OnboardingAllocation', usedBalance: 'onboardingUsedUnits', requestName: 'Onboarding Survey' },
  { key: 'Engagement', name: 'txtEngagement', image: '/assets/img/sub/Engagement.svg', tenantBalance: 'engagementBalance', currentBalance: 'EngagementAllocation', usedBalance: 'engagementUsedUnits', requestName: 'Engagement Survey' },
  { key: 'Happiness', name: 'txtEmployeeHappiness', image: '/assets/img/sub/Happiness.svg', tenantBalance: 'happinessBalance', currentBalance: 'HappinessAllocation', usedBalance: 'happinessUsedUnits', requestName: 'Happiness Survey' },
  { key: 'Wellbeing', name: 'txtWellbeing', image: '/assets/img/sub/Wellbeing.svg', tenantBalance: 'wellbeingBalance', currentBalance: 'WellBeingAllocation', usedBalance: 'wellbeingUsedUnits', requestName: 'Wellbeing Survey' },
  { key: 'Culture', name: 'txtCulture', image: '/assets/img/sub/Culture.svg', tenantBalance: 'cultureBalance', currentBalance: 'CultureAllocation', usedBalance: 'cultureUsedUnits', requestName: 'Culture Survey' },
  { key: 'AiReadiness', name: 'txtAiReadiness', image: '/assets/img/sub/AiReadiness.svg', tenantBalance: 'aiReadinessBalance', currentBalance: 'AiReadinessAllocation', usedBalance: 'aiReadinessUsedUnits', requestName: 'AI Readiness Survey' },
  { key: 'Assessment', name: 'txt360180_Feedback', image: '/assets/img/sub/360.svg', tenantBalance: 'assessmentBalance', currentBalance: 'AssessmentAllocation', usedBalance: 'assessmentUsedUnits', requestName: 'Assessment Survey' },
  { key: 'Exit', name: 'txt_employee_exit', image: '/assets/img/sub/Exit.svg', tenantBalance: 'exitBalance', currentBalance: 'ExitAllocation', usedBalance: 'exitUsedUnits', requestName: 'Exit Questionnaire' },
  { key: 'SurveyX', name: 'txtSurveyx', image: '/assets/img/sub/SurveyX.svg', tenantBalance: 'surveyxBalance', currentBalance: 'SurveyxAllocation', usedBalance: 'surveyxUsedUnits', requestName: 'Survey X' },

];

/** Purchase/allocation history field for a product, e.g. `exittransactionHistory`. */
export const transactionHistoryField = (productKey: string) => {
  let productKeyLower = productKey.toLowerCase();

  if (productKey === 'AiReadiness') {
    productKeyLower = 'aiReadiness';
  }

  return productKeyLower + 'transactionHistory';
};

/** Credit type the balance endpoints expect for a product, e.g. `SURVEYX`. */
export const creditTypeOf = (productKey: string) => productKey.toUpperCase();
